import { ReportingAccountService } from '@http/clients/reporting-app/reporting-account.service';
import { TabNames } from '@models/enums/TabNames';
import { GridDisplayScheme, OverviewLayout } from '@pages/reports/types/report-layout-type.enum';
import { WidgetType } from '@pages/reports/types/report-widget-type.enum';
import { formatCurrencyCompact, truncateValue } from '@utils/format-functions';
import { AgChartLegendLabelFormatterParams } from 'ag-charts-community';
import { ICellRendererParams } from 'ag-grid-enterprise';
import { IReportConfiguration } from '../../types/report-config.interface';
import { ReportType } from '../../types/report-type.enum';
import {
    accountEditorConfig,
    customerEditorConfig,
    departmentEditorConfig,
    includeArchivedEditorConfig,
    includeOpportunityArchivedEditorConfig,
    marketsEditorConfig,
    opportunityDueDateEditorConfig,
    opportunitySourceEditorConfig,
    projectEndDateEditorConfig,
    projectStartDateEditorConfig,
    salesPersonsEditorConfig
} from './shared/additional-filters';
import { crmMainFilters } from './shared/main-filters';

export const Accounts: IReportConfiguration<ReportingAccountService> = {
    base: {
        type: ReportType.Accounts,
        serviceType: ReportingAccountService
    },
    overviewTab: {
        widgets: [
            {
                methodName: 'getAccountBoxesValues',
                type: WidgetType.Box,
                layoutTemplateArea: 'boxes'
            },
            {
                methodName: 'getAccountMetricsValues',
                type: WidgetType.Metrics,
                layoutTemplateArea: 'metrics',
                settings: {
                    cols: [
                        { name: 'avg', label: 'Avg' },
                        { name: 'min', label: 'Min' },
                        { name: 'med', label: 'Med' },
                        { name: 'max', label: 'Max' }
                    ],
                    rows: [{ name: 'AwardedAmount', label: 'Awarded Amount', format: 'currency' }]
                }
            },
            {
                methodName: 'getAccountLineChartValues',
                type: WidgetType.Chart,
                layoutTemplateArea: 'chart',
                settings: {
                    title: {
                        text: 'Top 10 Accounts by Awarded Amount'
                    },
                    series: [
                        {
                            type: 'line',
                            xKey: 'accountName',
                            yKey: 'awardedAmount',
                            yName: 'Awarded Amount'
                        },
                        {
                            type: 'line',
                            xKey: 'accountName',
                            yKey: 'awardedCount',
                            yName: 'Awarded Count'
                        },
                        {
                            type: 'line',
                            xKey: 'accountName',
                            yKey: 'opportunityQty',
                            yName: 'Opportunities Count'
                        }
                    ],
                    axes: [
                        {
                            type: 'category',
                            position: 'bottom',
                            label: {
                                formatter: (params: AgChartLegendLabelFormatterParams) =>
                                    truncateValue(params.value, 15),
                                rotation: 315
                            }
                        },
                        {
                            type: 'number',
                            position: 'left',
                            title: {
                                text: 'Amount'
                            },
                            keys: ['awardedAmount'],
                            label: {
                                formatter: (params: AgChartLegendLabelFormatterParams) =>
                                    formatCurrencyCompact(params.value)
                            }
                        },
                        {
                            type: 'number',
                            position: 'right',
                            title: {
                                text: 'Quantity'
                            },
                            keys: ['awardedCount', 'opportunityQty']
                        }
                    ]
                }
            },
            {
                methodName: 'getAccountColumnChartValues',
                type: WidgetType.Chart,
                layoutTemplateArea: 'chart2',
                settings: {
                    title: {
                        text: 'Salesperson Performance'
                    },
                    series: [
                        {
                            type: 'bar',
                            xKey: 'salesPerson',
                            yKey: 'awardedAmount',
                            yName: 'Awarded Amount'
                        }
                    ],
                    axes: [
                        {
                            type: 'category',
                            position: 'bottom',
                            label: {
                                formatter: (params: AgChartLegendLabelFormatterParams) =>
                                    truncateValue(params.value, 15),
                                rotation: 315
                            }
                        },
                        {
                            type: 'number',
                            position: 'left',
                            title: {
                                text: 'Amount'
                            },
                            keys: ['awardedAmount'],
                            label: {
                                formatter: (params: AgChartLegendLabelFormatterParams) =>
                                    formatCurrencyCompact(params.value)
                            }
                        }
                    ]
                }
            }
        ],
        layout: OverviewLayout.ExtendedTwoPlusOne
    },
    gridTab: {
        widgets: [
            {
                methodName: 'getAccountValues',
                type: WidgetType.Grid,
                layoutTemplateArea: 'grid',
                settings: [
                    {
                        field: 'entityId',
                        headerName: 'Account ID',
                        lockVisible: true,
                        enableRowGroup: true,
                        cellRenderer: (params: ICellRendererParams) => {
                            if (params.value) {
                                const link = `/accounts-single/${params.value}/${TabNames[TabNames.Overview]}`;
                                return `<a class="anchor" href="${link}" target="_blank" rel="noopener noreferrer">${params.value}</a>`;
                            }
                            return `<div></div>`;
                        }
                    },
                    { field: 'accountName', headerName: 'Account Name', lockVisible: true, enableRowGroup: true },
                    { field: 'accountType', headerName: 'Account Type', enableRowGroup: true },
                    { field: 'accountMarket', headerName: 'Account Market' },
                    { field: 'opportunityQty', headerName: 'Opportunity Qty (Customer)', aggFunc: 'sum' },
                    { field: 'salesPersons', headerName: 'Salespersons', enableRowGroup: true },
                    { field: 'awardedCount', headerName: 'Awarded Count', aggFunc: 'sum' },
                    { field: 'awardedAmount', headerName: 'Awarded Amount', type: 'currency', aggFunc: 'sum' },
                    { field: 'organization', headerName: 'Organization', enableRowGroup: true },
                    { field: 'condition', headerName: 'Condition', enableRowGroup: true },
                    {
                        field: 'accountLastUpdatedNote',
                        headerName: 'Last Updated Note',
                        tooltipField: 'accountLastUpdatedNote'
                    },
                    { field: 'modificationDate', headerName: 'Modification Date', type: 'date' }
                ]
            }
        ],
        layout: GridDisplayScheme.Standard
    },
    filters: {
        main: [...crmMainFilters],
        additional: [
            opportunityDueDateEditorConfig,
            projectStartDateEditorConfig,
            projectEndDateEditorConfig,
            marketsEditorConfig,
            opportunitySourceEditorConfig,
            departmentEditorConfig,
            customerEditorConfig,
            accountEditorConfig,
            salesPersonsEditorConfig,
            includeOpportunityArchivedEditorConfig,
            includeArchivedEditorConfig
        ]
    }
};
